import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  Td,
  Text,
  useToast,
} from "@chakra-ui/react";
import React from "react";
import { FaTimes } from "react-icons/fa";
import CreateTable from "../components/common/Table/Table";
import Loading from "../components/common/ui/Loading";
import { downloadPurchaseRecords, getPurchaseRecords } from "../utils/api";
import useStore from "../utils/store";

const PurchaseRecords = () => {
  const [data, setData] = React.useState({});
  const limit = 50;
  const total = data?.metadata?.totalData;
  const [loading, setLoading] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const [search, setSearch] = React.useState("");
  const [searchValue, setSearchValue] = React.useState("");
  const toast = useToast();

  // Initialize search from URL parameters
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get("search");
    if (searchParam) {
      setSearchValue(searchParam);
      setSearch(searchParam);
    }
  }, []);

  const getAllData = async () => {
    setLoading(true);
    const [success, data] = await getPurchaseRecords(page, search);
    if (success) {
      window.scrollTo(0, 0);

      setData(data);
    }
    setLoading(false);
  };

  console.log(data);
  const user = useStore((state) => state.user);

  // Debounced search effect
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearch(searchValue);
      setPage(1); // Reset to first page when searching

      // Update URL with search parameter
      const url = new URL(window.location);
      if (searchValue) {
        url.searchParams.set("search", searchValue);
      } else {
        url.searchParams.delete("search");
      }
      window.history.replaceState({}, "", url);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchValue]);

  React.useEffect(() => {
    getAllData();
  }, [page, search]);

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const clearSearch = () => {
    setSearchValue("");
    setSearch("");
    setPage(1);

    // Remove search parameter from URL
    const url = new URL(window.location);
    url.searchParams.delete("search");
    window.history.replaceState({}, "", url);
  };

  const downloadPurchaseRecordsHandler = async () => {
    setLoading(true);
    try {
      const [success, data] = await downloadPurchaseRecords();
      if (success) {
        // Create a blob URL and trigger download
        const blob = new Blob([data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "purchase-records.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast({
          title: "Success",
          description: "Purchase records downloaded successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to download purchase records",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to download purchase records",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
    setLoading(false);
  };

  return user?.getPurchaseRecords ? (
    <>
      {loading ? (
        <Loading />
      ) : (
        <>
          {" "}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "20px",
            }}
          >
            <h1
              style={{
                textAlign: "center",
                fontSize: "20px",
                fontWeight: "bold",
                marginBlock: "20px",
              }}
            >
              Purchase Records
              {search && (
                <Text as="span" fontSize="14px" color="gray.500" ml={2}>
                  (Searching for: "{search}")
                </Text>
              )}
            </h1>
            <HStack spacing={4}>
              <Button
                onClick={downloadPurchaseRecordsHandler}
                colorScheme="blue"
                variant="solid"
                size="md"
              >
                Download CSV
              </Button>
              <Input
                placeholder="Search by name..."
                value={searchValue}
                onChange={handleSearchChange}
                width="300px"
                size="md"
              />
              {searchValue && (
                <IconButton
                  icon={<FaTimes />}
                  aria-label="Clear search"
                  onClick={clearSearch}
                  size="md"
                />
              )}
            </HStack>
          </div>
          <CreateTable
            data={
              data
                ? data?.data?.map((item) => {
                    return {
                      name: item?.user?.name,
                      subscription: item?.subscription?.title,
                      price: item?.subscription?.price,
                      date: item?.purchasedDate,
                    };
                  })
                : []
            }
            head={["Name", "Subscription", "Price", "Date"]}
            rows={(row) => {
              return (
                <>
                  <Td>{row?.name}</Td>
                  <Td>{row?.subscription}</Td>
                  <Td>{row?.price}</Td>
                  <Td>{new Date(row?.date ?? "").toLocaleDateString()}</Td>
                </>
              );
            }}
          />
          {search && data?.data?.length === 0 && (
            <Box textAlign="center" py={8}>
              <Text color="gray.500" fontSize="lg">
                No purchase records found for "{search}"
              </Text>
            </Box>
          )}
          <Box
            sx={{
              padding: "20px",
            }}
          >
            <Text
              fontSize="sm"
              color="gray.500"
              sx={{
                marginTop: 3,
                marginBottom: 3,
              }}
            >
              {`Showing ${page * limit - limit + 1} to ${
                page * limit > total ? total : page * limit
              } of ${total} entries`}
            </Text>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 3,

                paddingBottom: 5,
                overflow: "auto",
              }}
            >
              {new Array(Math?.ceil(total / limit))
                ?.fill(0)
                ?.map((item, index) => (
                  <Button
                    key={index}
                    onClick={() => setPage(index + 1)}
                    colorScheme={page === index + 1 ? "blue" : "gray"}
                    variant="solid"
                  >
                    {index + 1}
                  </Button>
                ))}
            </Box>
          </Box>
        </>
      )}
    </>
  ) : (
    <p style={{ textAlign: "center", color: "red", padding: "20px" }}>
      You don't have permission to access this page
    </p>
  );
};

export default PurchaseRecords;
